import styled, { css } from 'styled-components';
import { flexColumn, when } from '@stylesheets/helpers';
import Text, { TEXT_VARIANT_LABEL_SMALL } from '@components/1-atoms/Text';
import {
  DROP_DOWN_MENU_POSITION_TOP_LEFT,
  DROP_DOWN_MENU_POSITION_TOP_CENTER,
  DROP_DOWN_MENU_POSITION_TOP_RIGHT,
  DROP_DOWN_MENU_POSITION_BOTTOM_LEFT,
  DROP_DOWN_MENU_POSITION_BOTTOM_CENTER,
  DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT,
  DROP_DOWN_MENU_POSITION_BOTTOM_LEFT_EDGE,
  DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT_EDGE,
} from '@constants/dropDownMenuConstants';

export const OverlayLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

export const OuterWrapper = styled.div`
  display: none;
  min-height: 60px;
  max-height: 400px;
  ${flexColumn}
  opacity: 0;
  visibility: hidden;

  ${({
    $fixedPosition,
    $position,
    $show,
    style,
    $offsetX,
    $wrapperWidth = 240,
    $hasColumns,
  }) => css`
    width: ${$hasColumns ? 'auto' : `${$wrapperWidth}px`};
    padding: 0 ${$offsetX ? `${$offsetX}px` : 0};

    ${when(
      !style?.width && !$hasColumns,
      `
        max-width: 280px;
      `,
    )}

    ${when(
      !$fixedPosition,
      `
        position: absolute;

        ${when(
          [
            DROP_DOWN_MENU_POSITION_TOP_LEFT,
            DROP_DOWN_MENU_POSITION_TOP_CENTER,
            DROP_DOWN_MENU_POSITION_TOP_RIGHT,
          ].includes($position),
          `
            bottom: calc(100% + 10px);
            transform: translate(0, -10px);
          `,
        )}

        ${when(
          [
            DROP_DOWN_MENU_POSITION_BOTTOM_LEFT,
            DROP_DOWN_MENU_POSITION_BOTTOM_CENTER,
            DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT,
          ].includes($position),
          `
            top: calc(100% + 10px);
            transform: translate(0, 10px);
          `,
        )}

        ${when(
          [
            DROP_DOWN_MENU_POSITION_TOP_LEFT,
            DROP_DOWN_MENU_POSITION_BOTTOM_LEFT,
          ].includes($position),
          `
            left: 0;
          `,
        )}

        ${when(
          [
            DROP_DOWN_MENU_POSITION_TOP_CENTER,
            DROP_DOWN_MENU_POSITION_BOTTOM_CENTER,
          ].includes($position),
          `
            left: 50%;
          `,
        )}

        ${when(
          DROP_DOWN_MENU_POSITION_TOP_CENTER === $position,
          `
            left: 50%;
            transform: translate(-50%, -10px);
          `,
        )}

        ${when(
          DROP_DOWN_MENU_POSITION_BOTTOM_CENTER === $position,
          `
            left: 50%;
            transform: translate(-50%, 10px);
          `,
        )}

        ${when(
          [
            DROP_DOWN_MENU_POSITION_TOP_RIGHT,
            DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT,
          ].includes($position),
          `
            right: 0;
          `,
        )}

        ${when(
          $show,
          `
            transform: translate(0, 0);

            ${when(
              [
                DROP_DOWN_MENU_POSITION_TOP_CENTER,
                DROP_DOWN_MENU_POSITION_BOTTOM_CENTER,
              ].includes($position),
              `
                transform: translate(-50%, 0);
              `,
            )}
          `,
        )}

        ${when(
          DROP_DOWN_MENU_POSITION_BOTTOM_LEFT_EDGE === $position,
          `
            top: 0;
            left: unset;
            right: calc(100% + 10px);
            transform: translate(0, 0);
          `,
        )}

        ${when(
          DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT_EDGE === $position,
          `
            top: 0;
            right: unset;
            left: calc(100% + 10px);
            transform: translate(0, 0);
          `,
        )}
      `,
    )}

    ${when(
      $show,
      `
        display: flex;
        opacity: 1;
        visibility: visible;
      `,
    )}
  `}
`;

export const Wrapper = styled.div`
  ${flexColumn}
  overflow: auto;
  outline: none;

  ${({ theme: { borderRadius, spacing, colors }, $hasColumns }) => css`
    border-radius: ${borderRadius.small};
    background-color: ${colors.surfaceContainerHigh};
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.3),
      0px 2px 6px 2px rgba(0, 0, 0, 0.15);
    padding: ${$hasColumns ? 0 : `${spacing.level3} 0`};
  `}
`;

export const DraggableWrapper = styled.div``;

export const DraggableWrapperClone = styled.div`
  transform: none !important;
  pointer-events: none;

  & ~ div {
    transform: none !important;
  }
`;

export const DndPlaceholder = styled.div`
  display: none;
`;

export const ColumnsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

export const Column = styled.div`
  ${flexColumn}
  min-width: 200px;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level3} 0;
  `}

  &:not(:last-child) {
    ${({ theme: { colors } }) => css`
      border-right: 1px solid ${colors.outlineVariant};
    `}
  }
`;

export const ColumnHeader = styled(Text).attrs({
  variant: TEXT_VARIANT_LABEL_SMALL,
})`
  ${({ theme: { spacing, colors } }) => css`
    padding: ${spacing.level2} ${spacing.level4};
    color: ${colors.onSurfaceVariant};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `}
`;
